import {
  Button,
  FormInput,
  FormRoot,
  FormSelect,
  FormTextarea,
  RHF,
  zodResolver,
} from "@devhop/ui";
import { useQuery } from "@tanstack/react-query";
import { SparklesIcon, WalletIcon } from "lucide-react";
import { useEffect } from "react";
import { z } from "zod";
import { config, isDevMode } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { FormInfiniteCombobox } from "@/shared/ui/FormInfiniteCombobox";
import { ROLE_FEE_KIND_LABELS, RoleFeeKindSchema, type RoleFeeKind } from "../../domain/contracts";

type InstructorItem = { id: string; name: string };
type InstructorDetail = {
  id: string;
  name: string;
  email: string | null;
  phoneNumber: string | null;
};

const PerformerFormSchema = z.object({
  name: z.string().min(1, "ຕ້ອງປ້ອນຊື່ນັກສະແດງ"),
  phoneNumber: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  bankName: z.string().nullable().optional(),
  bankAccount: z.string().nullable().optional(),
  accountName: z.string().nullable().optional(),
  instructorId: z.string().nullable().optional(),
  status: z.enum(["active", "inactive"]),
  performerType: z.enum(["dancer", "choreographer", "kol", "other"]),
  defaultRoleFeeKind: RoleFeeKindSchema,
});

export type PerformerFormValues = z.infer<typeof PerformerFormSchema>;

const DEFAULT_ROLE_FEE_KIND_OPTIONS: Array<{
  value: RoleFeeKind;
  label: string;
}> = (
  Object.entries(ROLE_FEE_KIND_LABELS) as Array<[RoleFeeKind, string]>
).map(([value, label]) => ({ value, label }));

type PerformerFormProps = {
  initialValues?: Partial<PerformerFormValues>;
  onSubmit: (values: PerformerFormValues) => void;
  onCancel?: () => void;
  submitting?: boolean;
  isEdit?: boolean;
};

export function PerformerForm({
  initialValues,
  onSubmit,
  onCancel,
  submitting,
  isEdit = false,
}: PerformerFormProps) {
  const methods = RHF.useForm<PerformerFormValues>({
    resolver: zodResolver(PerformerFormSchema),
    defaultValues: {
      name: initialValues?.name ?? "",
      phoneNumber: initialValues?.phoneNumber ?? "",
      email: initialValues?.email ?? "",
      notes: initialValues?.notes ?? "",
      bankName: initialValues?.bankName ?? "",
      bankAccount: initialValues?.bankAccount ?? "",
      accountName: initialValues?.accountName ?? "",
      instructorId: initialValues?.instructorId ?? null,
      status: initialValues?.status ?? "active",
      performerType: initialValues?.performerType ?? "other",
      defaultRoleFeeKind: initialValues?.defaultRoleFeeKind ?? "performer",
    },
  });

  const watchedInstructorId = methods.watch("instructorId");

  const { data: instructorDetail } = useQuery({
    queryKey: ["master", "instructors", "detail", watchedInstructorId],
    queryFn: () =>
      fetcher.get<InstructorDetail>(
        `${config.apiUrl}/master/instructors/${watchedInstructorId}`,
      ),
    enabled: !!watchedInstructorId && !isEdit,
  });

  useEffect(() => {
    if (!isEdit && instructorDetail) {
      methods.setValue("name", instructorDetail.name, { shouldDirty: true });
      methods.setValue("email", instructorDetail.email ?? "", {
        shouldDirty: true,
      });
      methods.setValue("phoneNumber", instructorDetail.phoneNumber ?? "", {
        shouldDirty: true,
      });
    }
  }, [instructorDetail, isEdit]);

  const handleAutoFill = () => {
    methods.reset({
      name: "ສົມສະໜຸກ ພົມມາວັນ",
      email: "somsanuk@example.com",
      phoneNumber: "02099991234",
      notes: "ນັກຮ້ອງ / ນັກເຕັ້ນ Hip-hop. ມີປະສົບການ 5 ປີ.",
      bankName: "BCEL",
      bankAccount: "010123456789",
      accountName: "ສົມສະໜຸກ ພົມມາວັນ",
      instructorId: null,
      status: "active",
      performerType: "other",
      defaultRoleFeeKind: "performer",
    });
  };

  return (
    <FormRoot methods={methods} onSubmit={onSubmit} className="flex flex-col">
      <div className="space-y-0 divide-y">
        <div className="grid grid-cols-1 gap-6 py-8 md:grid-cols-[240px_1fr]">
          <div>
            <p className="font-semibold text-sm uppercase tracking-wide">
              ຂໍ້ມູນຕັວຕນ
            </p>
            <p className="mt-1 text-muted-foreground text-xs leading-relaxed">
              ຂໍ້ມູນຫຼັກ ແລະ ຂໍ້ມູນຕິດຕໍ່ຂອງນັກສະແດງ
            </p>
          </div>
          <div className="space-y-4">
            <FormInput
              name="name"
              label="ຊື່ເຕັມ"
              placeholder="ຕົວຢ່າງ: ສົມສະໜຸກ ພົມມາວັນ"
              requiredMark
            />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormInput
                name="email"
                label="ອີເມລ"
                placeholder="ຕົວຢ່າງ: name@example.com"
                type="email"
              />
              <FormInput
                name="phoneNumber"
                label="ເບີໂທ"
                placeholder="ຕົວຢ່າງ: 020xxxxxxxx"
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 py-8 md:grid-cols-[240px_1fr]">
          <div>
            <p className="flex items-center gap-2 font-semibold text-sm uppercase tracking-wide">
              <WalletIcon className="size-4 text-primary" />
              ບັນຊີຮັບເງິນ
            </p>
            <p className="mt-1 text-muted-foreground text-xs leading-relaxed">
              ຂໍ້ມູນທະນາຄານສຳລັບໃບເບີກຈ່າຍ — ຈະ prefilled ຕອນສ້າງໃບເບີກຈ່າຍ
            </p>
          </div>
          <div className="space-y-4">
            <FormInput
              name="bankName"
              label="ທະນາຄານ"
              placeholder="ຕົວຢ່າງ: BCEL, JDB"
            />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormInput
                name="bankAccount"
                label="ເລກບັນຊີ"
                placeholder="ຕົວຢ່າງ: 010xxxxxxxx"
              />
              <FormInput
                name="accountName"
                label="ຊື່ບັນຊີ"
                placeholder="ຊື່ຕາມບັນຊີທະນາຄານ"
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 py-8 md:grid-cols-[240px_1fr]">
          <div>
            <p className="font-semibold text-sm uppercase tracking-wide">
              ການຈັດການ
            </p>
            <p className="mt-1 text-muted-foreground text-xs leading-relaxed">
              ເຊື່ອມຕໍ່ຄູສອນ ແລະ ບັນທຶກໝາຍເຫດການດຳເນີນງານ
            </p>
          </div>
          <div className="space-y-4">
            <FormInfiniteCombobox<InstructorItem>
              name="instructorId"
              label="ເຊື່ອມຕໍ່ຄູສອນ"
              placeholder="ຄົ້ນຫາຊື່ຄູສອນ..."
              clearable
              queryKey={["master", "instructors", "lookup"]}
              queryFn={({ search, pageParam }) =>
                fetchLookupForInfinite<InstructorItem>(
                  `${config.apiUrl}/master/instructors/lookup`,
                  { search, pageParam },
                )
              }
              preloadQueryFn={(id) =>
                hydrateLookupItem<InstructorItem>(
                  `${config.apiUrl}/master/instructors/lookup`,
                  id,
                ).then((item) => item ?? null)
              }
              getLabel={(item) => item.name}
              getValue={(item) => item.id}
            />
            <p className="text-muted-foreground text-xs">
              {!isEdit
                ? "ເລືອກຄູສອນເພື່ອ auto-fill ຊື່, ອີເມລ ແລະ ເບີໂທໃນ Identity section ໂດຍອັດຕະໂນມັດ"
                : "ການເຊື່ອມຕໍ່ຄູສອນຊ່ວຍໃຫ້ລະບົບຕິດຕາມການສະແດງ ແລະ ອັດຕະໂນມັດການຄິດໄລ່ຄ່າຕອບແທນ"}
            </p>
            <FormTextarea
              name="notes"
              label="ໝາຍເຫດດ້ານການດຳເນີນງານ"
              placeholder="ຄວາມຊ່ຽວຊານ, ປະຫວັດ ຫຼື ຂໍ້ຄວນລະວັງ..."
              rows={4}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 py-8 md:grid-cols-[240px_1fr]">
          <div>
            <p className="font-semibold text-sm uppercase tracking-wide">
              ບົດບາດຄ່າຕອບແທນ
            </p>
            <p className="mt-1 text-muted-foreground text-xs leading-relaxed">
              ບົດບາດເລີ່ມຕົ້ນເວລາເພີ່ມນັກສະແດງເຂົ້າງານ — ສາມາດປັບໃໝ່ໃນຫນ້າງານໄດ້
            </p>
          </div>
          <div className="max-w-md">
            <FormSelect
              name="defaultRoleFeeKind"
              label="ບົດບາດເລີ່ມຕົ້ນ"
              options={DEFAULT_ROLE_FEE_KIND_OPTIONS}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 py-8 md:grid-cols-[240px_1fr]">
          <div>
            <p className="font-semibold text-sm uppercase tracking-wide">
              ສະຖານະ
            </p>
            <p className="mt-1 text-muted-foreground text-xs leading-relaxed">
              ກຳນົດວ່ານັກສະແດງສາມາດຖືກຈ້າງໃນງານໄດ້ຫຼືບໍ່
            </p>
          </div>
          <div className="grid max-w-md grid-cols-1 gap-4 sm:grid-cols-2">
            <FormSelect
              name="performerType"
              label="ປະເພດນັກສະແດງ"
              options={[
                { value: "dancer", label: "ນັກເຕັ້ນ" },
                { value: "choreographer", label: "Choreographer" },
                { value: "kol", label: "KOL" },
                { value: "other", label: "ອື່ນໆ" },
              ]}
            />
            <FormSelect
              name="status"
              label="ສະຖານະ"
              options={[
                { value: "active", label: "ໃຊ້ງານ (Active)" },
                { value: "inactive", label: "ບໍ່ໃຊ້ງານ (Inactive)" },
              ]}
            />
          </div>
        </div>
      </div>

      <div className="sticky bottom-0 z-10 flex items-center justify-between gap-4 border-t bg-background px-0 py-4">
        <p className="text-muted-foreground text-xs">
          ບົດບາດເລີ່ມຕົ້ນຈະ pre-fill ຕອນຈັດທີມໃນຫນ້າງານສະແດງ.
        </p>
        <div className="flex items-center gap-2">
          {isDevMode && (
            <Button
              type="button"
              variant="outline"
              onClick={handleAutoFill}
              disabled={submitting}
            >
              <SparklesIcon className="size-4" />
              Auto Fill
            </Button>
          )}
          {onCancel && (
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={submitting}
            >
              ຍົກເລີກ
            </Button>
          )}
          <Button type="submit" disabled={submitting}>
            {submitting ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກ"}
          </Button>
        </div>
      </div>
    </FormRoot>
  );
}
